import { type ReactNode, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { FadeUp, FormDialog } from '@core/components';
import {
  ORDER_ITEM_REGISTRY,
  type OrderItemFormValues,
  emptyOrderItemFormValues,
  getFormValuesCategory,
} from '../../utils/orderItem';
import { ORDER_ITEM_DIALOGS } from '../../utils/orderItem/dialogs';
import { getSummarySegmentTextColor } from '../../utils/orderItem/helpers';
import OrderItemDialogContent from './OrderItemDialogContent';

export interface OrderItemDialogProps {
  open: boolean;
  variant: 'add' | 'edit';
  initialValues: OrderItemFormValues;
  orderId?: string | number | null;
  orderCustomerId?: string | number | null;
  expandedSection?: string;
  onSubmit: (values: OrderItemFormValues) => Promise<void>;
  onClose: () => void;
}

const OrderItemDialog = ({
  open,
  variant,
  initialValues,
  orderId,
  orderCustomerId,
  expandedSection,
  onSubmit,
  onClose,
}: OrderItemDialogProps) => {
  const category = useMemo(
    () => getFormValuesCategory(initialValues),
    [initialValues]
  );
  const config = category ? ORDER_ITEM_REGISTRY[category] : undefined;
  const dialog = category ? ORDER_ITEM_DIALOGS[category] : undefined;

  const resetValues = useMemo(() => {
    if (!category) {
      return initialValues;
    }
    // On add, initialValues may be a draft; reset clears back to empty.
    return variant === 'add'
      ? emptyOrderItemFormValues(category, orderCustomerId)
      : initialValues;
  }, [category, variant, orderCustomerId, initialValues]);

  if (!category || !config || !dialog) {
    return null;
  }

  return (
    <FormDialog<OrderItemFormValues>
      open={open}
      onClose={onClose}
      title={variant === 'add' ? `Add ${config.label}` : `Edit ${config.label}`}
      defaultValues={initialValues}
      onSubmit={onSubmit}
      submitText={variant === 'add' ? 'Add' : 'Update'}
      maxWidth="lg"
      resetValues={resetValues}
    >
      <OrderItemDialogContent
        variant={variant}
        category={category}
        orderId={orderId}
        orderCustomerId={orderCustomerId}
        expandedSection={expandedSection}
      />
    </FormDialog>
  );
};

export function OrderItemSummaryRow({
  label,
  fadeKey,
  colorHex,
  children,
}: {
  label: string;
  fadeKey?: string;
  colorHex?: string | null;
  children: ReactNode;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Box
        sx={(theme) => {
          const color = getSummarySegmentTextColor(
            theme,
            colorHex?.trim() || undefined
          );
          return {
            textAlign: 'right',
            ...(color ? { '& .MuiTypography-root': { color } } : {}),
          };
        }}
      >
        <FadeUp key={fadeKey}>{children}</FadeUp>
      </Box>
    </Box>
  );
}

export default OrderItemDialog;
